/**
 * Em qual fatura uma compra cai — RN-23.
 *
 * ================================================================
 * POR QUE ISTO SAIU DO FORMULÁRIO
 * ================================================================
 *
 * A primeira versão vivia dentro do `TransactionForm` e estava errada em um mês
 * inteiro. Ela somava sempre `+1` para achar o mês do vencimento, e isso só vale
 * quando o cartão vence ANTES de fechar no calendário:
 *
 *   fecha 25, vence 10  →  vencimento no mês SEGUINTE ao ciclo   ✔ acertava
 *   fecha  7, vence 12  →  vencimento no MESMO mês do ciclo      ✘ errava
 *
 * Num cartão que fecha dia 7 e vence dia 12, uma compra de 06/08 fecha em 07/08
 * e vence em 12/08 — cinco dias depois. A prévia anunciava setembro.
 *
 * O backend sempre teve a regra certa (`CardCycleService::cycleDates`), e o
 * formulário de cartão também (o cálculo do intervalo entre fechar e vencer).
 * A prévia era o único lugar que não sabia — porque era o único sem teste.
 *
 * Aqui é função pura, sem React, testada nos dois casos. É o que impede a
 * terceira cópia da regra de divergir de novo.
 *
 * ================================================================
 * SÓ DATAS
 * ================================================================
 *
 * Nenhum valor monetário é calculado aqui (ADR-0003). E o DIA exato do
 * vencimento não é prometido: fim de semana e feriado bancário empurram para o
 * dia útil seguinte (RN-24), e a tabela de feriados vive no servidor.
 */

export type CardCycleInput = {
  /** Dia em que a fatura fecha. */
  closingDay: number;
  /** Dia em que a fatura vence. */
  dueDay: number;
  /** Data da compra, em AAAA-MM-DD. */
  purchaseDate: string;
};

export type CardCycle = {
  /**
   * O nome da fatura, como AAAA-MM.
   *
   * É o mesmo identificador que a tela de Cartões mostra — o sistema batiza a
   * fatura pelo CICLO, não pelo vencimento. Chamá-la de outra coisa aqui faria
   * a pessoa procurar uma fatura que não existe com aquele nome.
   */
  reference: string;
  /** O mês do ciclo, por extenso: "agosto de 2026". */
  cycleLabel: string;
  /** O mês em que ela vence, por extenso. Pode ser o mesmo do ciclo. */
  dueLabel: string;
  /** Verdadeiro quando fecha e vence dentro do mesmo mês. */
  duesInSameMonth: boolean;
};

const MES = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });

export function cardCycleFor({
  closingDay,
  dueDay,
  purchaseDate,
}: CardCycleInput): CardCycle | null {
  if (!purchaseDate || !Number.isFinite(closingDay) || !Number.isFinite(dueDay)) {
    return null;
  }

  // Meio-dia evita que fuso horário jogue a data para o dia anterior.
  const compra = new Date(`${purchaseDate}T12:00:00`);

  if (Number.isNaN(compra.getTime())) {
    return null;
  }

  // Até o dia do fechamento, o ciclo é o do mês da compra; depois, o seguinte.
  // `<=` e não `<`: comprar NO dia do fechamento ainda é desta fatura.
  const ciclo = new Date(compra.getFullYear(), compra.getMonth(), 1);

  if (compra.getDate() > closingDay) {
    ciclo.setMonth(ciclo.getMonth() + 1);
  }

  /**
   * A REGRA QUE FALTAVA, e é a mesma do backend.
   *
   * `dueDay > closingDay` significa que o vencimento cai depois do fechamento
   * dentro do mesmo mês — fecha dia 7, vence dia 12. Caso contrário o
   * vencimento já virou o mês: fecha dia 25, vence dia 10 do mês que vem.
   */
  const duesInSameMonth = dueDay > closingDay;

  const vence = new Date(ciclo.getFullYear(), ciclo.getMonth() + (duesInSameMonth ? 0 : 1), 1);

  return {
    reference: `${ciclo.getFullYear()}-${String(ciclo.getMonth() + 1).padStart(2, '0')}`,
    cycleLabel: MES.format(ciclo),
    dueLabel: MES.format(vence),
    duesInSameMonth,
  };
}
